import { ExternalLink } from '@ipfabric/ui-components/Link/Link';
import type { NonEmptyArray } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { getVisibleColumnsDependencies } from 'components/ApiTable/helpers/getVisibleColumnsDependencies';
import { useApiTableContextSelectors } from 'components/ApiTable/model/ApiTableModel';
import { CopyToClipboard } from 'components/CopyToClipboard/CopyToClipboard';
import { useLocale } from 'containers/App/LocaleContext';
import { produce } from 'immer';
import { useMemo } from 'react';
import type { FC } from 'react';
import ReactMarkdown from 'react-markdown';
import { apiEndpointToFullUrl } from 'utils/apiEndpointToFullUrl';
import { getDocsUrlForTableEndpoint } from '../helpers/getDocsUrlForTableEndpoint';

export const ApiTableEndpointDocumentation: FC = () => {
  const descriptionMsg = useLocale().apiTable.description;
  const ctxProps = useApiTableContextSelectors({
    endpoint: prop('endpoint'),
    dispatch: prop('dispatch'),
    lastRequestPayload: prop('lastRequestPayload'),
    renderCustomApiDoc: prop('renderCustomApiDoc'),
    tableDescription: prop('tableDescription'),
    columnVisibility: prop('tableModel', 'settings', 'columnVisibility'),
  });

  const endpointUrl = apiEndpointToFullUrl(ctxProps.endpoint.apiEndpoint);
  const apiDocsUrl = getDocsUrlForTableEndpoint(ctxProps.endpoint.apiEndpoint);

  const requestPayload = useMemo(
    () =>
      ctxProps.lastRequestPayload
        ? JSON.stringify(
            produce(ctxProps.lastRequestPayload, (draft) => {
              draft.pagination = {
                start: 0,
                limit: 100,
              };
              draft.columns = getVisibleColumnsDependencies(
                ctxProps.columnVisibility,
                ctxProps.endpoint,
                undefined, // no need to fetch row id to just show data
              ) as NonEmptyArray<string>;
            }),
            null,
            2,
          )
        : null,
    [ctxProps.lastRequestPayload, ctxProps.columnVisibility, ctxProps.endpoint],
  );

  const description = (() => {
    if (ctxProps.tableDescription) {
      return ctxProps.tableDescription;
    }
    if (ctxProps.endpoint.description) {
      return (
        <ReactMarkdown className='styled-lists'>
          {ctxProps.endpoint.description}
        </ReactMarkdown>
      );
    }
    return null;
  })();

  return (
    <>
      {description}
      {requestPayload && (
        <>
          <h3>
            {descriptionMsg.requestPayloadTitle}
            <CopyToClipboard text={requestPayload} />
          </h3>
          <pre className='whitespace-pre-wrap break-words'>
            {requestPayload}
          </pre>
        </>
      )}
      <h3>{descriptionMsg.apiDocumentationTitle}</h3>
      {ctxProps.renderCustomApiDoc && <h4>{descriptionMsg.readTitle}</h4>}
      <p>
        <strong>URL:</strong>{' '}
        <code className='whitespace-pre-wrap break-words'>{endpointUrl}</code>
        <CopyToClipboard text={endpointUrl} />
      </p>
      <p>
        <ExternalLink href={apiDocsUrl}>API documentation</ExternalLink>
      </p>
      {ctxProps.renderCustomApiDoc?.()}
    </>
  );
};
